import styled from "styled-components";
import global from "../../global";

export const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  background-color: transparent;
  min-height: 220px;
`;

export const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 12px;
  min-width: 600px;
  text-align: left;

  @media (max-width: ${global.responsive.small.width.value}${global.responsive.small.width.unit}) {
    display: block;
    min-width: 100%;
    border-spacing: 0;

    thead {
      display: none;
    }

    tbody {
      display: block;
      width: 100%;
    }
  }
`;

export const Thead = styled.thead`
  background: transparent !important;
  border: none;
  
  tr, th {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
  }
  
  tr {
    pointer-events: none; /* Previne que o hover do tr reaja aqui */
  }
`;

export const Th = styled.th<{ colWidth?: string; $clickable?: boolean }>`
  padding: 0 20px;
  height: auto;
  font-weight: 500;
  font-size: 14px; /* Idêntico ao MiniHeader do dashboard */
  color: var(--gray);
  vertical-align: middle;
  ${props => props.colWidth && `width: ${props.colWidth};`}
  ${props =>
    props.$clickable &&
    `
      pointer-events: auto;
      cursor: pointer;
      user-select: none;
      transition: color 0.15s ease;
      &:hover {
        color: var(--fixit);
      }
    `}

  &.actions-header {
    width: 100px;
  }
`;

export const Tbody = styled.tbody``;

interface TrProps {
  clickable?: boolean;
}

export const Tr = styled.tr<TrProps>`
  background-color: var(--white);
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.03);
  transition: all 0.2s ease;
  cursor: ${(props) => (props.clickable ? "pointer" : "default")};

  /* Bordas arredondadas e contorno nos cantos dos cards (HTML Table Hack) */
  td {
    border-top: 1px solid rgba(0, 0, 0, 0.04);
    border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  }
  td:first-child {
    border-left: 1px solid rgba(0, 0, 0, 0.04);
    border-radius: 16px 0 0 16px;
  }
  td:last-child {
    border-right: 1px solid rgba(0, 0, 0, 0.04);
    border-radius: 0 16px 16px 0;
  }

  &:hover {
    background-color: var(--white);
    transform: ${(props) => (props.clickable ? "translateY(-2px)" : "none")};
    box-shadow: ${(props) => (props.clickable ? "0px 8px 20px rgba(0, 0, 0, 0.06)" : "0px 4px 12px rgba(0, 0, 0, 0.03)")};
  }

  &:has(.table-actions-dropdown) {
    position: relative;
    z-index: 15;
  }

  @media (max-width: ${global.responsive.small.width.value}${global.responsive.small.width.unit}) {
    display: flex;
    flex-direction: column;
    padding: 16px;
    border: 1px solid rgba(0, 0, 0, 0.04);
    border-radius: 16px;
    margin-bottom: 12px;
    position: relative; /* Importante para as ações flutuarem */

    &:last-child {
      margin-bottom: 0;
    }
    
    td {
      border: none;
    }
    td:first-child {
      border: none;
      border-radius: 0;
    }
    td:last-child {
      border: none;
      border-radius: 0;
    }
  }
`;

export const Td = styled.td<{ colWidth?: string }>`
  padding: 12px 20px;
  height: auto;
  min-height: 64px;
  font-size: 14px;
  color: var(--dark-gray);
  vertical-align: middle;
  border-bottom: none;
  ${props => props.colWidth && `width: ${props.colWidth};`}

  &.actions-cell {
    width: 100px;
  }

  @media (max-width: ${global.responsive.small.width.value}${global.responsive.small.width.unit}) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    padding: 10px 0;
    border-bottom: none;
    height: auto;
    min-height: auto;
    padding-right: 40px; /* Garante que o texto não atropele o botão flutuante */
    width: 100% !important;

    &:last-child {
      padding-bottom: 0;
    }

    &::before {
      content: attr(data-label);
      font-weight: 500;
      color: var(--gray);
      font-size: 12px;
      margin-bottom: 4px;
      margin-right: 0;
    }

    &.actions-cell {
      position: absolute;
      top: 16px;
      right: 16px;
      width: auto;
      height: auto;
      min-height: auto;
      padding: 0;
      
      &::before {
        display: none;
      }
    }
  }
`;

export const ActionButton = styled.button<{ isOpen?: boolean }>`
  background: transparent;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 50%;
  color: ${(props) => props.isOpen ? "var(--fixit)" : "var(--primary)"};
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
`;

export const ActionsContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-start;
`;

export const ActionsWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

export const ActionsDropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  width: 210px;
  border-radius: 10px;
  background: var(--white);
  box-shadow: rgba(0, 0, 0, 0.15) 0px 5px 15px;
  z-index: 50;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

export const ActionOption = styled.div<{ disabled?: boolean }>`
  width: 100%;
  height: 40px;
  padding: 4px 16px;

  display: flex;
  align-items: center;
  gap: 10px;

  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  transition: all 0.3s;
  background: var(--white);

  svg {
    width: 18px;
    height: 18px;
    color: var(--primary);
  }

  span {
    font-size: 15px;
    line-height: 19px;
    color: var(--primary);
  }

  &:hover {
    background: ${props => props.disabled ? 'var(--white)' : '#f5f5f5'};
  }
`;
